import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { FieldArray, Formik } from 'formik';
import { get, isArray, isEmpty, uniqueId } from 'lodash';
import clsx from 'clsx';
import { ClipboardIcon } from '@heroicons/react/24/outline';
import { GripVertical, X } from 'lucide-react';

import Features from '@dashboard/enums/features';
import usePage from '@dashboard/hooks/usePage';
import { confirm } from '@dashboard/lib/confirm';

import { countries } from '@dashboard/services/countries/countries';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import {
  elementFieldTypeEnums,
  getDataFieldTypesForElement,
  getElement,
  getElementFieldTypes,
} from '@dashboard/services/pageFieldTypes';

import Button from '@dashboard/components/Button';
import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import Disclosure from '@dashboard/components/Formik/components/Disclosure';
import CopyTextWithTooltip from '@dashboard/components/CopyTextWithTooltip';
import { FormElement, Label, LabelDescription } from '@dashboard/components/Form';
import { Checkbox, FormSubmit, Listbox, Select, TextInput } from '@dashboard/components/Formik';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import Link from '@dashboard/components/Link';
import Reorderable from '@dashboard/components/Reorderable';
import { InfoTooltip } from '@dashboard/components/Tooltip';
import { Separator } from '@dashboard/components/ui/separator';

import { useFieldEdit } from '../FieldEditContext';
import { useFieldHelpers } from '../useFieldHelpers';
import ShowHideLogicField from './ShowHideLogicField';
import { useFields } from '../FieldsContext';
import { useFieldSourceSettings } from '../FieldSourceContext';
import {
  defaultSelectionChoices,
  keepsBounds,
  layoutForField,
  normalizeSelections,
  pruneDefaultSelections,
  validateDefaultSelections,
  validateSelections,
  validateUniqueOptions,
} from '../selectionRules';
import AddressFieldSettings from './FieldEdit/AddressFieldSettings';
import FieldTypeButton from './FieldEdit/FieldTypeButton';
import SelectionsField from './FieldEdit/SelectionsField';
import fieldFormValues from '../fieldFormValues';
import { UnsavedChangesReporter } from '@dashboard/components/UnsavedChanges';

const FormElementInput = styled.div`
  width: 100%;
`;

const propTypes = {
  onSubmit: PropTypes.func.isRequired,
  field: PropTypes.object.isRequired,
  actions: PropTypes.element.isRequired,
  disabled: PropTypes.bool,
};

const FieldEdit = ({ field, fieldKey, disabled }) => {
  const { page } = usePage();
  const { onCancelAddField, onSaveField, onDirty, showRemoveField } = useFieldHelpers(
    field._id || field.key,
  );
  const { setIsEditingField } = useFieldEdit();

  const { state: allFields } = useFields();
  const { allowedElements, isLocked, hasLayout } = useFieldSourceSettings();
  const elementFieldTypes = allowedElements ?? getElementFieldTypes(page.type);
  const isLockedGuestQuestion = isLocked(field);

  // Default email field (the only email field, not removable) is locked to
  // the email element + email data type — no point in letting users change
  // either and get into a broken state.
  const isDefaultEmailField = field.element === elementFieldTypeEnums.EMAIL && !showRemoveField;
  const typeIsFixed = isDefaultEmailField || isLockedGuestQuestion;

  return (
    <div>
      <Formik
        initialValues={fieldFormValues(field)}
        onSubmit={async (formValues, actions) => {
          // Formik hands over its live state: deleting the editor-only keys on it
          // flashes Selections allowed back to "One" until the post-save reset lands.
          const values = { ...formValues };
          actions.setStatus(undefined);

          // `selectionMode` only drives the editor; the element and bounds carry it.
          const selectionMode = values.selectionMode;
          delete values.selectionMode;

          if (values.element !== elementFieldTypeEnums.MULTI_SELECT) {
            delete values.layout;
          }

          const optionErrors = validateUniqueOptions(values);
          if (!isEmpty(optionErrors)) {
            actions.setErrors(optionErrors);
            return;
          }

          if (!keepsBounds(values.element)) {
            // Back from a multi-select: stale counts must not linger on the field.
            values.minValue = { enabled: false, value: '' };
            values.maxValue = { enabled: false, value: '' };
          }

          if (values.element === elementFieldTypeEnums.MULTI_SELECT) {
            const optionCount = (values.options ?? []).filter(option => option.label).length;
            const selectionErrors = validateSelections({
              mode: selectionMode,
              minValue: values.minValue,
              maxValue: values.maxValue,
              optionCount,
            });
            if (!isEmpty(selectionErrors)) {
              actions.setErrors(selectionErrors);
              return;
            }
            const bounds = normalizeSelections(selectionMode, values.minValue, values.maxValue);
            values.minValue = bounds.minValue;
            values.maxValue = bounds.maxValue;
            values.defaultValue = pruneDefaultSelections(values.defaultValue, values.options);
            const defaultErrors = validateDefaultSelections({
              defaultValue: values.defaultValue,
              maxValue: values.maxValue,
              options: values.options,
            });
            if (!isEmpty(defaultErrors)) {
              actions.setErrors(defaultErrors);
              return;
            }
            // Required is independent of the counts: it says whether an answer is needed at all.
            values.required = Boolean(values.required);
          }

          // Clear stale data type when the new element doesn't support it.
          // Without this, changing an Email field's element to Address keeps
          // type='email' on the Address field, which causes the backend's
          // "An email field is required" guard to refuse deletion of the
          // address — leaving an undeletable address-element field that
          // disables Address in the field-type dropdown.
          const validDataTypes = getDataFieldTypesForElement(values.element);
          if (!validDataTypes || validDataTypes.length === 0) {
            values.type = undefined;
          } else if (values.type && !validDataTypes.some(t => t.value === values.type)) {
            values.type = undefined;
          }

          // Convert string boolean values to actual booleans for checkbox elements
          if (values.element === 'checkbox' && values.defaultValue?.enabled) {
            if (values.defaultValue.value === 'true') {
              values.defaultValue.value = true;
            } else if (values.defaultValue.value === 'false') {
              values.defaultValue.value = false;
            }
          }

          // Empty string from the "Detect from visitor" option means "not set".
          // Send null (not undefined) so Mongoose findOneAndUpdate actually
          // clears any previously-saved defaultCountry; undefined gets dropped
          // from the $set payload and the old value sticks.
          if (values.element === elementFieldTypeEnums.ADDRESS && values.defaultCountry === '') {
            values.defaultCountry = null;
          }

          // On the page form, address and email fields are always required; a guest's are its own call.
          if (
            hasLayout &&
            (values.element === elementFieldTypeEnums.ADDRESS ||
              values.element === elementFieldTypeEnums.EMAIL)
          ) {
            values.required = true;
          }

          const { error, data } = await onSaveField(values);

          if (error) {
            const { status, errors } = transformApiErrorToFormik(error);

            if (!isEmpty(errors)) {
              actions.setErrors(errors);
            } else {
              actions.setStatus(status);
            }
          } else {
            // Convert string boolean values to actual booleans for checkbox elements
            if (data.element === 'checkbox' && data.defaultValue?.enabled) {
              if (data.defaultValue.value === 'true') {
                data.defaultValue.value = true;
              } else if (data.defaultValue.value === 'false') {
                data.defaultValue.value = false;
              }
            }

            actions.resetForm({ values: fieldFormValues(data) });
          }
        }}
      >
        {({ handleSubmit, dirty, status, values, setFieldValue, errors }) => {
          const dataFieldTypes = getDataFieldTypesForElement(values.element);

          useEffect(() => {
            const element = getElement(values.element);

            if (field.isNew && !values.label && element.defaultLabel) {
              setFieldValue('label', element.defaultLabel);
            }

            if (field.isNew && !values.placeholder && element.defaultPlaceholder) {
              if (values.element === 'phone') {
                return;
              }

              setFieldValue('placeholder', element.defaultPlaceholder);
            }

            {
              /* // reset default value for country field
            if (values.element === 'country' && values.defaultValue?.value) {
              setFieldValue('defaultValue.value', '');
              setFieldValue('defaultValue.enabled', false);
            } */
            }
          }, [values.element]);

          onDirty(dirty);

          return (
            <form onSubmit={handleSubmit}>
              <UnsavedChangesReporter />
              <div className="mb-4">
                {!typeIsFixed && (
                  <FormElement>
                    <Label for={`${fieldKey}_element`}>Field type *</Label>
                    <FormElementInput>
                      <FieldTypeButton
                        id={`${fieldKey}_element`}
                        name="element"
                        allowedElements={elementFieldTypes}
                        fields={allFields}
                        currentKey={field._id || field.key}
                      />
                    </FormElementInput>
                    <FormError name="element" />
                  </FormElement>
                )}

                {values.element !== elementFieldTypeEnums.ADDRESS && (
                  <FormElement>
                    <Label for={`${fieldKey}_label`}>Label *</Label>
                    <FormElementInput>
                      <TextInput
                        id={`${fieldKey}_label`}
                        name="label"
                        type="text"
                        clearable
                        placeholder="Label"
                        autoFocus={!!field.isNew}
                      />
                    </FormElementInput>
                    <FormError name="label" />
                  </FormElement>
                )}

                {values.element === elementFieldTypeEnums.ADDRESS && (
                  <AddressFieldSettings
                    fieldKey={fieldKey}
                    values={values}
                    setFieldValue={setFieldValue}
                  />
                )}

                {values.element !== 'checkbox' &&
                  values.element !== 'quantity' &&
                  values.element !== elementFieldTypeEnums.MULTIPLE_CHOICE &&
                  layoutForField(values) !== 'list' &&
                  values.element !== elementFieldTypeEnums.ADDRESS && (
                    <FormElement>
                      <Label for={`${fieldKey}_placeholder`}>Placeholder</Label>
                      <FormElementInput>
                        <TextInput
                          id={`${fieldKey}_placeholder`}
                          name="placeholder"
                          type="text"
                          clearable
                          placeholder="Placeholder"
                        />
                      </FormElementInput>
                      <FormError name="placeholder" />
                    </FormElement>
                  )}

                {supportsDescription(values.element) && (
                  <FormElement>
                    <Label for={`${fieldKey}_description`}>Description</Label>
                    <FormElementInput>
                      <TextInput
                        id={`${fieldKey}_description`}
                        name="description"
                        type="text"
                        clearable
                        placeholder="Description"
                      />
                    </FormElementInput>
                    <LabelDescription className="mt-1 mb-0">
                      Shown under the field in small text
                    </LabelDescription>
                    <FormError name="description" />
                  </FormElement>
                )}

                {(values.element === elementFieldTypeEnums.SELECT ||
                  values.element === elementFieldTypeEnums.MULTIPLE_CHOICE ||
                  values.element === elementFieldTypeEnums.MULTI_SELECT) && (
                  <Separator className="mb-4" />
                )}

                {layoutForField(values) === 'dropdown' &&
                  (values.element === elementFieldTypeEnums.SELECT ||
                    values.element === elementFieldTypeEnums.MULTI_SELECT) && (
                    <SelectField
                      options={values.options}
                      onChange={options => setFieldValue('options', options)}
                      field={field}
                      fieldKey={fieldKey}
                      errors={errors}
                    />
                  )}

                {layoutForField(values) === 'list' &&
                  (values.element === elementFieldTypeEnums.MULTIPLE_CHOICE ||
                    values.element === elementFieldTypeEnums.MULTI_SELECT) && (
                    <OptionsField
                      options={values.options}
                      onChange={options => setFieldValue('options', options)}
                      field={field}
                      fieldKey={fieldKey}
                      errors={errors}
                    />
                  )}

                {(values.element === elementFieldTypeEnums.SELECT ||
                  values.element === elementFieldTypeEnums.MULTIPLE_CHOICE ||
                  values.element === elementFieldTypeEnums.MULTI_SELECT) && (
                  <SelectionsField
                    fieldKey={fieldKey}
                    values={values}
                    setFieldValue={setFieldValue}
                  />
                )}

                {dataFieldTypes && dataFieldTypes.length > 0 && !typeIsFixed && hasLayout && (
                  <FormElement>
                    <Label for={`${fieldKey}_type`}>
                      Data type
                      <InfoTooltip>
                        Used to store field data in a predictable manner. Please note that you must
                        use Customer Name and Shipping Address if you wish to use any of the
                        shipping address data types.
                      </InfoTooltip>
                    </Label>

                    <FormElementInput>
                      <Select name="type" id={`${fieldKey}_type`}>
                        <option value="">Select data type</option>
                        {getDataFieldTypesForElement(values.element).map((type, index) => (
                          <option key={index} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Select>
                    </FormElementInput>

                    <FormError name="type" />
                  </FormElement>
                )}

                {(hasLayout
                  ? values.element !== 'quantity' &&
                    values.element !== elementFieldTypeEnums.ADDRESS &&
                    values.element !== elementFieldTypeEnums.EMAIL
                  : values.type !== 'email') && (
                  <FormElement>
                    <Checkbox.Wrapper>
                      <Checkbox id={`${fieldKey}_required`} name="required" />
                      <Checkbox.Label htmlFor={`${fieldKey}_required`}>Required</Checkbox.Label>
                    </Checkbox.Wrapper>

                    <FormError name="required" />
                  </FormElement>
                )}

                <div className="mt-4 flex flex-col gap-2">
                  <Disclosure
                    label="Conditional logic"
                    compactRow
                    nested
                    defaultOpen={Boolean(field.showHideLogic?.enabled)}
                  >
                    <div>
                      <Checkbox.Wrapper>
                        <Checkbox
                          id={`${fieldKey}_showHideLogic.enabled`}
                          name="showHideLogic.enabled"
                        />
                        <Checkbox.Label htmlFor={`${fieldKey}_showHideLogic.enabled`}>
                          Show this field based on another field{' '}
                          <InfoTooltip>
                            <Link
                              to="/docs/articles/conditional-logic-for-custom-fields"
                              target="_blank"
                              variant="light"
                            >
                              Learn more
                            </Link>
                          </InfoTooltip>
                        </Checkbox.Label>
                      </Checkbox.Wrapper>
                      <FormError name="showHideLogic.enabled" />

                      {values.showHideLogic?.enabled && (
                        <ShowHideLogicField
                          fieldKey={fieldKey}
                          field={field}
                          fields={hasLayout ? page.fields : allFields.filter(f => f._id)}
                        />
                      )}
                    </div>
                  </Disclosure>

                  <Disclosure
                    label="Additional settings"
                    compactRow
                    nested
                    // Open when the saved field already uses one, so an active setting is never hidden.
                    defaultOpen={Boolean(
                      field.hidden ||
                        field.limitAllowedCountries?.enabled ||
                        field.defaultValue?.enabled ||
                        field.minValue?.enabled ||
                        field.maxValue?.enabled,
                    )}
                  >
                    <FormElement className="space-y-2 mb-4">
                      {(values.element === 'country' || values.element === 'phone') && (
                        <>
                          <Checkbox.Wrapper>
                            <Checkbox
                              id={`${fieldKey}_limitAllowedCountries.enabled`}
                              name="limitAllowedCountries.enabled"
                            />
                            <Checkbox.Label htmlFor={`${fieldKey}_limitAllowedCountries.enabled`}>
                              Limit allowed countries
                            </Checkbox.Label>
                          </Checkbox.Wrapper>

                          {values.limitAllowedCountries?.enabled && (
                            <FormElement className="ml-[26px] !mb-4">
                              <FormElementInput>
                                <Listbox
                                  id={`${fieldKey}_limitAllowedCountries.countries`}
                                  name="limitAllowedCountries.countries"
                                  options={countries.map(country => ({
                                    name: country.name,
                                    value: country.alpha2,
                                  }))}
                                  value={values.limitAllowedCountries.countries}
                                  onChange={countries => {
                                    // if only one country, enable showSelectedDialCode
                                    if (
                                      countries.length === 1 &&
                                      values.element === 'phone' &&
                                      values.showSelectedDialCode === undefined
                                    ) {
                                      setFieldValue('showSelectedDialCode', true);
                                    }
                                  }}
                                  placeholder="Select allowed countries"
                                  multiple
                                  selectAll
                                  selectAllLabel="All countries"
                                />
                              </FormElementInput>
                            </FormElement>
                          )}

                          <FormError name="allowedCountries" />
                        </>
                      )}

                      {values.element === 'phone' &&
                        values.limitAllowedCountries?.enabled &&
                        values.limitAllowedCountries.countries?.length === 1 && (
                          <Checkbox.Wrapper>
                            <Checkbox
                              id={`${fieldKey}_showSelectedDialCode`}
                              name="showSelectedDialCode"
                            />
                            <Checkbox.Label htmlFor={`${fieldKey}_showSelectedDialCode`}>
                              Show dial code
                            </Checkbox.Label>
                          </Checkbox.Wrapper>
                        )}

                      {values.element !== elementFieldTypeEnums.ADDRESS && (
                        <Checkbox.Wrapper>
                          <Checkbox
                            id={`${fieldKey}_defaultValue.enabled`}
                            name="defaultValue.enabled"
                          />
                          <Checkbox.Label htmlFor={`${fieldKey}_defaultValue.enabled`}>
                            Default value
                          </Checkbox.Label>
                          <FormError name="defaultValue.enabled" />
                        </Checkbox.Wrapper>
                      )}

                      {values.defaultValue?.enabled && (
                        <FormElement className="ml-[26px] !mb-4">
                          {values.element === 'date-time' && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="datetime-local"
                              placeholder="Default value"
                            />
                          )}

                          {values.element === 'date' && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="date"
                              placeholder="Default value"
                            />
                          )}

                          {values.element === 'time' && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="time"
                              placeholder="Default value"
                            />
                          )}

                          {values.element === 'phone' && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="tel"
                              placeholder="Default value"
                            />
                          )}

                          {values.element === 'country' && (
                            <Select id={`${fieldKey}_defaultValue.value`} name="defaultValue.value">
                              <option value="" default>
                                Select option
                              </option>

                              {values.limitAllowedCountries?.enabled &&
                                values.limitAllowedCountries.countries?.map(country => (
                                  <option key={country} value={country}>
                                    {countries.find(c => c.alpha2 === country).name}
                                  </option>
                                ))}

                              {!values.limitAllowedCountries?.enabled &&
                                countries.map(country => (
                                  <option key={country.alpha2} value={country.alpha2}>
                                    {country.name}
                                  </option>
                                ))}
                            </Select>
                          )}

                          {values.element === 'checkbox' && (
                            <Checkbox.Wrapper>
                              <Checkbox
                                id={`${fieldKey}_defaultValue.value`}
                                name="defaultValue.value"
                                onChange={isChecked => {
                                  setFieldValue('defaultValue.value', isChecked);
                                }}
                                checked={
                                  values.defaultValue?.value !== undefined &&
                                  values.defaultValue?.value !== false &&
                                  values.defaultValue?.value !== 'false'
                                }
                              />
                              <Checkbox.Label htmlFor={`${fieldKey}_defaultValue.value`}>
                                Checked
                              </Checkbox.Label>
                            </Checkbox.Wrapper>
                          )}

                          {(values.element === 'multiple-choice' ||
                            values.element === 'select') && (
                            <Select id={`${fieldKey}_defaultValue.value`} name="defaultValue.value">
                              <option value="" default>
                                Select option
                              </option>
                              {values.options?.map(option => (
                                <option key={option.key} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          )}

                          {values.element === elementFieldTypeEnums.MULTI_SELECT && (
                            <Listbox
                              id={`${fieldKey}_defaultValue.values`}
                              name="defaultValue.values"
                              options={defaultSelectionChoices(values.options)}
                              value={values.defaultValue?.values ?? []}
                              placeholder="Select options"
                              emptyStateTitle="Add options first"
                              emptyStateDescription="The default is picked from the field's options."
                              multiple
                              summary
                            />
                          )}
                          {values.element === elementFieldTypeEnums.MULTI_SELECT && (
                            <FormError name="defaultValue.values" />
                          )}

                          {values.element === 'quantity' && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="number"
                              placeholder="Default value"
                            />
                          )}

                          {values.element === 'number' && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="number"
                              placeholder="Default value"
                            />
                          )}

                          {(values.element === 'text' ||
                            values.element === 'email' ||
                            values.element === 'textarea' ||
                            values.element === 'tax-id' ||
                            values.element === 'po-number') && (
                            <TextInput
                              id={`${fieldKey}_defaultValue.value`}
                              name="defaultValue.value"
                              type="text"
                              placeholder="Default value"
                            />
                          )}

                          <FormError name="defaultValue.value" />
                        </FormElement>
                      )}

                      <MinValueField fieldKey={fieldKey} values={values} />
                      <MaxValueField fieldKey={fieldKey} values={values} />

                      <CanUseFeature feature={Features.HIDE_FIELDS}>
                        <Checkbox.Wrapper>
                          <Checkbox id={`${fieldKey}_hidden`} name="hidden" />
                          <Checkbox.Label htmlFor={`${fieldKey}_hidden`}>
                            Hide field{' '}
                            <InfoTooltip>
                              Hide this field and pre-fill it with values from other pages & query
                              parameters.{' '}
                              <Link
                                to="/docs/articles/prefill-checkout-fields"
                                target="_blank"
                                variant="light"
                              >
                                Learn more
                              </Link>
                            </InfoTooltip>
                          </Checkbox.Label>
                        </Checkbox.Wrapper>

                        <FormError name="hidden" />
                      </CanUseFeature>
                    </FormElement>

                    <FormElement>
                      <Label htmlFor={`${fieldKey}_reference`}>
                        Reference
                        <InfoTooltip>
                          {hasLayout ? (
                            <>
                              Use this reference to pre-fill the form value and to use the value in
                              Zapier.{' '}
                              <Link
                                to="/docs/articles/prefill-checkout-fields"
                                target="_blank"
                                variant="light"
                              >
                                Learn more
                              </Link>
                            </>
                          ) : (
                            'An ID for this attendee question. It stays the same when you change the label.'
                          )}
                        </InfoTooltip>
                      </Label>
                      <FormElementInput className="relative">
                        <TextInput
                          id={`${fieldKey}_reference`}
                          name="reference"
                          type="text"
                          placeholder="Reference"
                        />

                        <div className="absolute top-0 right-2 bottom-0 flex items-center">
                          <CopyTextWithTooltip text={values.reference}>
                            <ClipboardIcon className="w-4 h-4" />
                          </CopyTextWithTooltip>
                        </div>
                      </FormElementInput>
                      <FormError name="reference" />
                    </FormElement>
                  </Disclosure>
                </div>
              </div>

              <FormErrorBase>{status && status}</FormErrorBase>

              <FormSubmit align="end">
                <FormSubmit.State visible={!!dirty} />
                <FormSubmit.Cancel
                  disabled={disabled}
                  onClick={() => {
                    if (field.isNew) {
                      onCancelAddField();
                    } else {
                      setIsEditingField(false, field._id || field.key);
                    }
                  }}
                >
                  Cancel
                </FormSubmit.Cancel>
                <FormSubmit.Button disabled={disabled}>Save</FormSubmit.Button>
              </FormSubmit>

              <div style={{ marginTop: '0.5em', color: 'var(--gray-darker)' }}>
                {field.error && field.error}
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

function supportsDescription(element) {
  switch (element) {
    case elementFieldTypeEnums.TEXT:
    case elementFieldTypeEnums.TEXTAREA:
    case elementFieldTypeEnums.EMAIL:
    case elementFieldTypeEnums.PHONE:
    case elementFieldTypeEnums.NUMBER:
    case elementFieldTypeEnums.DATE:
    case elementFieldTypeEnums.TIME:
    case elementFieldTypeEnums.DATE_TIME:
    case elementFieldTypeEnums.COUNTRY:
    case elementFieldTypeEnums.TAX_ID:
    case elementFieldTypeEnums.PO_NUMBER:
    case elementFieldTypeEnums.SELECT:
    case elementFieldTypeEnums.MULTI_SELECT:
    case elementFieldTypeEnums.MULTIPLE_CHOICE:
      return true;
    default:
      return false;
  }
}

function hasMaxValue(element) {
  switch (element) {
    case elementFieldTypeEnums.DATE_TIME:
    case elementFieldTypeEnums.DATE:
    case elementFieldTypeEnums.TIME:
    case elementFieldTypeEnums.QUANTITY:
      return true;
    default:
      return false;
  }
}

function hasMinValue(element) {
  switch (element) {
    case elementFieldTypeEnums.DATE_TIME:
    case elementFieldTypeEnums.DATE:
    case elementFieldTypeEnums.TIME:
    case elementFieldTypeEnums.QUANTITY:
      return true;
    default:
      return false;
  }
}

function getValueTypeForElement(element) {
  switch (element) {
    case elementFieldTypeEnums.DATE_TIME:
      return 'datetime-local';
    case elementFieldTypeEnums.DATE:
      return 'date';
    case elementFieldTypeEnums.TIME:
      return 'time';
    case elementFieldTypeEnums.QUANTITY:
      return 'number';
    default:
      return null;
  }
}

function MinValueField({ fieldKey, values }) {
  if (!hasMinValue(values.element)) return null;

  const valueType = getValueTypeForElement(values.element);

  return (
    <>
      <Checkbox.Wrapper>
        <Checkbox id={`${fieldKey}_minValue.enabled`} name="minValue.enabled" />
        <Checkbox.Label htmlFor={`${fieldKey}_minValue.enabled`}>Minimum value</Checkbox.Label>
        <FormError name="minValue.enabled" />
      </Checkbox.Wrapper>

      {values.minValue?.enabled && (
        <FormElement className="ml-[26px] !mb-4">
          <TextInput
            id={`${fieldKey}_minValue.value`}
            name="minValue.value"
            type={valueType}
            placeholder="Minimum value"
          />
          <FormError name="minValue.value" />
        </FormElement>
      )}
    </>
  );
}

function MaxValueField({ fieldKey, values }) {
  if (!hasMaxValue(values.element)) return null;

  const valueType = getValueTypeForElement(values.element);

  return (
    <>
      <Checkbox.Wrapper>
        <Checkbox id={`${fieldKey}_maxValue.enabled`} name="maxValue.enabled" />
        <Checkbox.Label htmlFor={`${fieldKey}_maxValue.enabled`}>Maximum value</Checkbox.Label>
        <FormError name="maxValue.enabled" />
      </Checkbox.Wrapper>

      {values.maxValue?.enabled && (
        <FormElement className="ml-[26px] !mb-4">
          <TextInput
            id={`${fieldKey}_maxValue.value`}
            name="maxValue.value"
            type={valueType}
            placeholder="Maximum value"
          />
          <FormError name="maxValue.value" />
        </FormElement>
      )}
    </>
  );
}

function OptionsField({ options = [], onChange, fieldKey, errors }) {
  const OPTION = {
    key: uniqueId(),
    label: '',
  };

  return (
    <FormElement>
      <Label for={`${fieldKey}_options`}>Options</Label>

      <FieldArray
        name="options"
        render={arrayHelpers => (
          <>
            <Reorderable
              id={`fields.${fieldKey}.options`}
              items={options}
              onReorder={onChange}
              renderItem={({ dragHandleProps, index }) => {
                const labelError = get(errors, `options.${index}.label`);

                return (
                  <div className="pb-2">
                    <div className="flex flex-row items-center gap-2">
                      <div
                        {...dragHandleProps}
                        className={clsx(
                          buttonVariants({ variant: 'ghost', size: 'icon' }),
                          'cursor-grab active:cursor-grabbing',
                        )}
                      >
                        <GripVertical className="size-4" />
                      </div>

                      <div className="flex-1">
                        <TextInput
                          id={`options.${index}.label`}
                          name={`options.${index}.label`}
                          type="text"
                          placeholder="Label*"
                          autoFocus={!options[index].label}
                        />
                      </div>

                      <ShadcnButton
                        variant="ghost"
                        size="icon"
                        type="button"
                        aria-label="Delete option"
                        onClick={async () => {
                          const del = await confirm('Delete this option?', {
                            confirm: 'Delete',
                          });

                          if (del) {
                            arrayHelpers.remove(index);
                          }
                        }}
                      >
                        <X className="size-4" />
                      </ShadcnButton>
                    </div>

                    {labelError && (
                      <div className="pl-10">
                        <FormErrorBase>{labelError}</FormErrorBase>
                      </div>
                    )}
                  </div>
                );
              }}
            />

            <Button
              handleClick={() => arrayHelpers.push(OPTION)}
              appearance="light"
              type="button"
              className="mt-2"
            >
              + Add option
            </Button>
          </>
        )}
      />

      {errors && !isArray(errors.options) && <FormError name="options" />}
    </FormElement>
  );
}

function SelectField({ options = [], onChange, fieldKey, errors }) {
  const SELECT_OPTION = {
    key: uniqueId(),
    label: '',
    value: '',
  };

  return (
    <FormElement>
      <Label for={`${fieldKey}_options`}>Options</Label>

      <FieldArray
        name="options"
        render={arrayHelpers => (
          <>
            <Reorderable
              id={`fields.${fieldKey}.options`}
              items={options}
              onReorder={onChange}
              renderItem={({ dragHandleProps, index }) => {
                const labelError = get(errors, `options.${index}.label`);
                const valueError = get(errors, `options.${index}.value`);

                return (
                  <div className="pb-2">
                    <div className="flex flex-row items-center gap-2">
                      <div
                        {...dragHandleProps}
                        className={clsx(
                          buttonVariants({ variant: 'ghost', size: 'icon' }),
                          'cursor-grab active:cursor-grabbing',
                        )}
                      >
                        <GripVertical className="size-4" />
                      </div>

                      <div className="flex-1">
                        <TextInput
                          id={`options.${index}.label`}
                          name={`options.${index}.label`}
                          type="text"
                          placeholder="Label*"
                          autoFocus={!options[index].label}
                        />
                      </div>

                      <div className="flex-1">
                        <TextInput
                          id={`options.${index}.value`}
                          name={`options.${index}.value`}
                          type="text"
                          placeholder="Value*"
                        />
                      </div>

                      <ShadcnButton
                        variant="ghost"
                        size="icon"
                        type="button"
                        aria-label="Delete option"
                        onClick={async () => {
                          const del = await confirm('Delete this option?', {
                            confirm: 'Delete',
                          });

                          if (del) {
                            arrayHelpers.remove(index);
                          }
                        }}
                      >
                        <X className="size-4" />
                      </ShadcnButton>
                    </div>

                    {(labelError || valueError) && (
                      <div className="flex flex-row gap-2 pl-10">
                        <div className="flex-1">
                          {labelError && <FormErrorBase>{labelError}</FormErrorBase>}
                        </div>
                        <div className="flex-1">
                          {valueError && <FormErrorBase>{valueError}</FormErrorBase>}
                        </div>
                        <div className="size-8 shrink-0" />
                      </div>
                    )}
                  </div>
                );
              }}
            />

            <Button
              handleClick={() => arrayHelpers.push(SELECT_OPTION)}
              appearance="light"
              type="button"
              className="mt-2"
            >
              + Add option
            </Button>
          </>
        )}
      />

      {errors && !isArray(errors.options) && <FormError name="options" />}
    </FormElement>
  );
}

FieldEdit.propTypes = propTypes;

export default FieldEdit;
